/**
 * Mobile-only "drill trail" — a tiny sessionStorage-backed memory of the
 * path the user took inside `MobileCategoryDrillDown` before they landed
 * on a results page (`/category/<key>` or `/category/<key>?subcategory=…`).
 *
 * Why this exists:
 *   The drill-down overlay lives inside Home / MobileCategories as local
 *   React state — once the user taps a leaf or "view all", the overlay
 *   unmounts and that state is gone. The results page (`CategoryPage`)
 *   then has no idea HOW the user got there, so its only "back" option
 *   is a generic browser-history pop or a hard link to "/" — neither of
 *   which respects the tree the user just walked through.
 *
 *   This module persists the drill path across that single navigation,
 *   purely on the client, so:
 *     1. CategoryPage can show a mobile-only "back to drill" button.
 *     2. Tapping it returns the user to the EXACT branch they were on
 *        (parent → child → grand-child), not to the categories grid.
 *
 * Why sessionStorage (and not URL / Redux / context):
 *   - URL: adding `?trail=a.b.c` to results URLs would pollute deep
 *     links, change SEO surface, and require encoding decisions.
 *   - Redux/context: would force every consumer (incl. desktop) to
 *     mount a new provider for what is a single-tab, mobile-only
 *     ephemeral piece of state.
 *   - sessionStorage: scoped to the tab, survives the one navigation
 *     we care about, auto-cleared on tab close, zero new providers,
 *     zero deps. Reads/writes are wrapped in try/catch because
 *     storage can throw in private mode / disabled cookies.
 *
 * Why two keys (`TRAIL_KEY` + `RESTORE_KEY`):
 *   - `TRAIL_KEY` is "what trail is currently associated with the
 *     active results page". CategoryPage reads this to decide whether
 *     to show the back button and where to send the user.
 *   - `RESTORE_KEY` is a one-shot signal: "next time Home or
 *     MobileCategories mounts, auto-open the drill at this path". It
 *     is consumed-and-cleared on read so a refresh of the parent page
 *     never accidentally re-pops the drill.
 *
 * Out of scope:
 *   - Cross-tab sync (intentionally NOT localStorage).
 *   - Anything beyond the drill → results → drill loop.
 */

const TRAIL_KEY = "iraqsooq.categoryTrail.v1";
const RESTORE_KEY = "iraqsooq.categoryTrail.restore.v1";

/** Where the drill-down was opened from. */
export type TrailReturnTo = "/" | "/categories";

export interface CategoryTrail {
  /** Top-level category key, e.g. "emlak" / "vasita". */
  categoryKey: string;
  /**
   * Node IDs walked from the root, in order. An empty array means the
   * user was still on the root level of the drill-down when they hit
   * "view all" — back from results should re-open the drill-down at
   * the root (showing top-level branches), then one more back closes it.
   */
  path: string[];
  /** URL of the page that hosted the drill-down (Home or Categories tab). */
  returnTo: TrailReturnTo;
}

function safeGet(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(key, value);
  } catch {
    // private mode / quota / disabled — best-effort only.
  }
}

function safeRemove(key: string): void {
  try {
    if (typeof window === "undefined") return;
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function parse(raw: string | null): CategoryTrail | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as Partial<CategoryTrail>;
    if (
      obj &&
      typeof obj.categoryKey === "string" &&
      Array.isArray(obj.path) &&
      obj.path.every((p) => typeof p === "string") &&
      (obj.returnTo === "/" || obj.returnTo === "/categories")
    ) {
      return { categoryKey: obj.categoryKey, path: obj.path, returnTo: obj.returnTo };
    }
    return null;
  } catch {
    return null;
  }
}

/** Save the trail right before navigating to a results page. */
export function setCategoryTrail(trail: CategoryTrail): void {
  safeSet(TRAIL_KEY, JSON.stringify(trail));
}

/** Read the active trail (does NOT clear it). */
export function getCategoryTrail(): CategoryTrail | null {
  return parse(safeGet(TRAIL_KEY));
}

/** Clear the trail — call when the user has finished the back loop. */
export function clearCategoryTrail(): void {
  safeRemove(TRAIL_KEY);
}

/**
 * Mark a "restore on next mount" request. Home / MobileCategories pick
 * this up the next time they mount and re-open the drill-down at the
 * stored path. Single-shot: `consumeCategoryRestore` clears it.
 */
export function setCategoryRestore(trail: CategoryTrail): void {
  safeSet(RESTORE_KEY, JSON.stringify(trail));
}

/**
 * Read AND clear the restore request. Returning the value while clearing
 * the storage in one call removes any "what if it fires twice?" gotcha
 * for callers (StrictMode double-invocation, route remounts, etc.).
 */
export function consumeCategoryRestore(): CategoryTrail | null {
  const value = parse(safeGet(RESTORE_KEY));
  if (value) safeRemove(RESTORE_KEY);
  return value;
}
